import React, { useState, useEffect, useCallback, useRef } from "react";
import { getJson, postJson } from "../../services/http";

// ── Constants ────────────────────────────────────────────────────────────────

const POLL_INTERVAL = 2000; // ms

const COMPONENT_COLORS = {
  Auth:          "#6f42c1",
  Tenants:       "#0d6efd",
  Users:         "#0dcaf0",
  Events:        "#198754",
  Communication: "#fd7e14",
  Workflow:      "#ffc107",
  Content:       "#d63384",
  Reporting:     "#20c997",
  Configuration: "#6c757d",
};

const STATUS_BADGE = {
  passed:   { bg: "#198754", label: "Passed" },
  failed:   { bg: "#dc3545", label: "Failed" },
  skipped:  { bg: "#6c757d", label: "Skipped" },
  timedOut: { bg: "#fd7e14", label: "Timed Out" },
  running:  { bg: "#0d6efd", label: "Running" },
  error:    { bg: "#dc3545", label: "Error" },
};

// ── Known error patterns → friendly messages ─────────────────────────────────

const ERROR_PATTERNS = [
  {
    pattern: /not found.*available projects.*""/i,
    title: "Playwright config not found",
    message: "The Playwright configuration file could not be loaded. The --config flag may be pointing to a missing file.",
    fix: "Ensure playwright.config.js exists in the project root and that @playwright/test is installed.",
  },
  {
    pattern: /playwright.*not found|cannot find.*playwright|ENOENT.*playwright/i,
    title: "Playwright is not installed",
    message: "The Playwright binary was not found on this server.",
    fix: "SSH into the server and run:\n  cd /var/www/html/on_screen\n  npm install -D @playwright/test\n  npx playwright install chromium --with-deps",
  },
  {
    pattern: /ERR_CONNECTION_REFUSED/i,
    title: "Application not reachable",
    message: "The test browser could not connect to the application. The frontend or backend server may not be running.",
    fix: "Ensure both the React dev server (port 3000) and Express server (port 5000) are running, or update BASE_URL in .env.test.local to match your deployment URL.",
  },
  {
    pattern: /TEST_OWNER_USER.*TEST_OWNER_PASS|env vars are required/i,
    title: "Test credentials not configured",
    message: "The TEST_OWNER_USER and TEST_OWNER_PASS environment variables are not set.",
    fix: "Add these to your server .env file or to .env.test.local in the project root:\n  TEST_OWNER_USER=your_username\n  TEST_OWNER_PASS=your_password",
  },
  {
    pattern: /Executable doesn.*exist|browserType\.launch|browser.*not.*install/i,
    title: "Chromium browser not installed",
    message: "Playwright needs a Chromium browser binary to run tests, but none was found.",
    fix: "Run on the server:\n  npx playwright install chromium --with-deps",
  },
  {
    pattern: /ETIMEDOUT|ECONNABORTED|net::ERR_TIMED_OUT/i,
    title: "Connection timed out",
    message: "The test browser timed out trying to reach the application.",
    fix: "Check that BASE_URL points to a reachable address and that the application is responding.",
  },
  {
    pattern: /401.*unauthorized|403.*forbidden|auth.*failed/i,
    title: "Authentication failed",
    message: "The test credentials were rejected by the server.",
    fix: "Verify TEST_OWNER_USER and TEST_OWNER_PASS in your .env.test.local are valid login credentials for an existing owner account.",
  },
];

function diagnoseError(stderr, error) {
  const text = `${stderr || ""} ${error || ""}`;
  for (const p of ERROR_PATTERNS) {
    if (p.pattern.test(text)) return p;
  }
  return null;
}

// ── Component ────────────────────────────────────────────────────────────────

export default function TestRunner({ tenantId }) {
  const [authorized, setAuthorized] = useState(false);
  const [health, setHealth] = useState(null);
  const [healthLoading, setHealthLoading] = useState(true);
  const [suites, setSuites] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [groupBy, setGroupBy] = useState("component"); // 'component' | 'type'
  const [activeRun, setActiveRun] = useState(null);
  const [runError, setRunError] = useState(null);
  const [runHistory, setRunHistory] = useState([]);
  const [expandedErrors, setExpandedErrors] = useState(new Set());
  const [expandedSuites, setExpandedSuites] = useState(new Set());
  const pollRef = useRef(null);

  // ── Auth check ────────────────────────────────────────────────────────────

  useEffect(() => {
    getJson("/api/super-admin/check-access")
      .then(() => setAuthorized(true))
      .catch(() => setAuthorized(false));
  }, []);

  // ── Health check + load suites ────────────────────────────────────────────

  useEffect(() => {
    if (!authorized) return;

    setHealthLoading(true);
    getJson("/api/test-runner/health")
      .then((data) => setHealth(data))
      .catch((err) => setHealth({ healthy: false, checks: [], error: err.message }))
      .finally(() => setHealthLoading(false));

    getJson("/api/test-runner/suites").then((data) => {
      setSuites(data.suites || []);
      setSelected(new Set((data.suites || []).map((s) => s.id)));
    });

    getJson("/api/test-runner/runs").then((data) => {
      setRunHistory(data.runs || []);
    });
  }, [authorized]);

  const refreshHealth = () => {
    setHealthLoading(true);
    getJson("/api/test-runner/health")
      .then((data) => setHealth(data))
      .catch((err) => setHealth({ healthy: false, checks: [], error: err.message }))
      .finally(() => setHealthLoading(false));
  };

  // ── Polling for active run ────────────────────────────────────────────────

  const pollRun = useCallback((runId) => {
    if (pollRef.current) clearInterval(pollRef.current);

    const poll = () => {
      getJson(`/api/test-runner/runs/${runId}`).then((data) => {
        setActiveRun(data.run);

        // Diagnose errors when run finishes
        if (data.run.status !== "running") {
          clearInterval(pollRef.current);
          pollRef.current = null;

          if (data.run.status === "error" || data.run.status === "failed") {
            const diagnosis = diagnoseError(data.run.rawStderr, null);
            setRunError(diagnosis);
          } else {
            setRunError(null);
          }

          getJson("/api/test-runner/runs").then((h) => setRunHistory(h.runs || []));
        }
      });
    };

    poll();
    pollRef.current = setInterval(poll, POLL_INTERVAL);
  }, []);

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  // ── Run tests ─────────────────────────────────────────────────────────────

  const handleRun = async () => {
    const suiteIds = [...selected];
    if (suiteIds.length === 0) return;

    setRunError(null);

    try {
      const data = await postJson("/api/test-runner/run", { suiteIds });
      if (data.runId) {
        setActiveRun({ id: data.runId, status: "running", suiteIds, startedAt: new Date().toISOString() });
        setExpandedErrors(new Set());
        setExpandedSuites(new Set());
        pollRun(data.runId);
      }
    } catch (err) {
      const errMsg = err?.data?.error || err?.message || String(err);
      const diagnosis = diagnoseError(errMsg, errMsg);
      setRunError(diagnosis || {
        title: "Failed to start test run",
        message: errMsg,
        fix: "Check the server logs for more details.",
      });
    }
  };

  // ── Selection helpers ─────────────────────────────────────────────────────

  const toggleSuite = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const selectAll = () => setSelected(new Set(suites.map((s) => s.id)));
  const selectNone = () => setSelected(new Set());

  const selectGroup = (key) => {
    const gField = groupBy === "component" ? "component" : "type";
    const ids = suites.filter((s) => s[gField] === key).map((s) => s.id);
    setSelected((prev) => {
      const next = new Set(prev);
      const allSelected = ids.every((id) => next.has(id));
      ids.forEach((id) => (allSelected ? next.delete(id) : next.add(id)));
      return next;
    });
  };

  // ── Grouping ──────────────────────────────────────────────────────────────

  const grouped = {};
  const field = groupBy === "component" ? "component" : "type";
  for (const s of suites) {
    const key = s[field] || "Other";
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(s);
  }

  // ── Toggle helpers ────────────────────────────────────────────────────────

  const toggleError = (idx) => {
    setExpandedErrors((prev) => {
      const next = new Set(prev);
      next.has(idx) ? next.delete(idx) : next.add(idx);
      return next;
    });
  };

  const toggleSuiteExpand = (suite) => {
    setExpandedSuites((prev) => {
      const next = new Set(prev);
      next.has(suite) ? next.delete(suite) : next.add(suite);
      return next;
    });
  };

  // ── Reload a past run ─────────────────────────────────────────────────────

  const loadRun = (runId) => {
    getJson(`/api/test-runner/runs/${runId}`).then((data) => {
      setActiveRun(data.run);
      setExpandedErrors(new Set());
      setExpandedSuites(new Set());
      if (data.run.status === "error" || data.run.status === "failed") {
        setRunError(diagnoseError(data.run.rawStderr, null));
      } else {
        setRunError(null);
      }
    });
  };

  // ── Render ────────────────────────────────────────────────────────────────

  if (!authorized) {
    return (
      <div className="container py-4">
        <div className="alert alert-danger">
          <i className="bi bi-shield-exclamation me-2"></i>
          <strong>Access Denied</strong> — Super admin access is required to use the Test Runner.
        </div>
      </div>
    );
  }

  const isRunning = activeRun?.status === "running";
  const healthIssues = health?.checks?.filter(c => !c.ok) || [];
  const hasHealthIssues = !health?.healthy && healthIssues.length > 0;

  return (
    <div className="container-fluid py-4" style={{ maxWidth: 1200 }}>
      {/* Header */}
      <div className="d-flex align-items-center justify-content-between mb-4">
        <div>
          <h3 className="mb-1">
            <i className="bi bi-bug me-2"></i>Playwright Test Runner
          </h3>
          <small className="text-muted">Regression test suite for OnScreen PWA</small>
        </div>
        <div className="d-flex gap-2 align-items-center">
          <button
            className="btn btn-outline-secondary btn-sm"
            onClick={refreshHealth}
            disabled={healthLoading}
            title="Re-check environment health"
          >
            <i className={`bi bi-arrow-clockwise ${healthLoading ? "spin" : ""}`}></i>
            {" "}Health
          </button>
          <div className="btn-group btn-group-sm">
            <button
              className={`btn ${groupBy === "component" ? "btn-primary" : "btn-outline-primary"}`}
              onClick={() => setGroupBy("component")}
            >
              By Component
            </button>
            <button
              className={`btn ${groupBy === "type" ? "btn-primary" : "btn-outline-primary"}`}
              onClick={() => setGroupBy("type")}
            >
              By Type
            </button>
          </div>
        </div>
      </div>

      {/* ── Health check banner ──────────────────────────────────────────── */}
      {healthLoading && !health && (
        <div className="alert alert-info d-flex align-items-center mb-4">
          <span className="spinner-border spinner-border-sm me-2"></span>
          Checking test environment...
        </div>
      )}

      {hasHealthIssues && (
        <div className="alert alert-warning mb-4" style={{ border: "1px solid #ffc107" }}>
          <div className="d-flex align-items-center mb-2">
            <i className="bi bi-exclamation-triangle-fill me-2" style={{ fontSize: 18 }}></i>
            <strong>Environment issues detected</strong>
            <small className="text-muted ms-2">— fix these before running tests</small>
          </div>
          {health.checks.map((check) => (
            <div
              key={check.id}
              className="d-flex align-items-start py-1"
              style={{ fontSize: 13 }}
            >
              <i
                className={`bi me-2 mt-1 ${check.ok ? "bi-check-circle-fill text-success" : "bi-x-circle-fill text-danger"}`}
              ></i>
              <div>
                <strong>{check.label}</strong>
                {!check.ok && (
                  <div className="text-muted" style={{ whiteSpace: "pre-wrap" }}>{check.detail}</div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {health && !hasHealthIssues && !healthLoading && (
        <div className="alert alert-success d-flex align-items-center mb-4 py-2" style={{ fontSize: 13 }}>
          <i className="bi bi-check-circle-fill me-2"></i>
          All {health.checks?.length || 0} environment checks passed — ready to run tests.
        </div>
      )}

      <div className="row g-4">
        {/* ── Left: Suite selection ────────────────────────────────────────── */}
        <div className="col-lg-4">
          <div className="card shadow-sm">
            <div className="card-header d-flex justify-content-between align-items-center">
              <strong>Test Suites</strong>
              <div className="btn-group btn-group-sm">
                <button className="btn btn-outline-secondary btn-sm" onClick={selectAll}>All</button>
                <button className="btn btn-outline-secondary btn-sm" onClick={selectNone}>None</button>
              </div>
            </div>
            <div className="card-body p-0" style={{ maxHeight: 500, overflowY: "auto" }}>
              {Object.entries(grouped).map(([groupName, items]) => {
                const allChecked = items.every((s) => selected.has(s.id));
                const someChecked = items.some((s) => selected.has(s.id));
                return (
                  <div key={groupName}>
                    {/* Group header */}
                    <div
                      className="d-flex align-items-center px-3 py-2"
                      style={{
                        backgroundColor: "#f8f9fa",
                        borderBottom: "1px solid #dee2e6",
                        cursor: "pointer",
                      }}
                      onClick={() => selectGroup(groupName)}
                    >
                      <input
                        type="checkbox"
                        className="form-check-input me-2"
                        checked={allChecked}
                        ref={(el) => { if (el) el.indeterminate = someChecked && !allChecked; }}
                        readOnly
                      />
                      <span
                        className="badge me-2"
                        style={{ backgroundColor: COMPONENT_COLORS[groupName] || "#6c757d" }}
                      >
                        {groupName}
                      </span>
                      <small className="text-muted ms-auto">{items.length} suite{items.length !== 1 ? "s" : ""}</small>
                    </div>
                    {/* Suite items */}
                    {items.map((suite) => (
                      <div
                        key={suite.id}
                        className="d-flex align-items-center px-3 py-2"
                        style={{
                          borderBottom: "1px solid #f0f0f0",
                          paddingLeft: 28,
                          cursor: "pointer",
                          backgroundColor: selected.has(suite.id) ? "#f0f7ff" : "transparent",
                        }}
                        onClick={() => toggleSuite(suite.id)}
                      >
                        <input
                          type="checkbox"
                          className="form-check-input me-2"
                          checked={selected.has(suite.id)}
                          readOnly
                        />
                        <span>{suite.name}</span>
                        <small className="text-muted ms-auto">{suite.id}</small>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
            <div className="card-footer">
              <button
                className="btn btn-success w-100"
                onClick={handleRun}
                disabled={isRunning || selected.size === 0}
              >
                {isRunning ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" />
                    Running {activeRun.suiteIds?.length || 0} suite(s)...
                  </>
                ) : (
                  <>
                    <i className="bi bi-play-fill me-1"></i>
                    Run {selected.size} Suite{selected.size !== 1 ? "s" : ""}
                  </>
                )}
              </button>
            </div>
          </div>

          {/* ── Run history ─────────────────────────────────────────────── */}
          {runHistory.length > 0 && (
            <div className="card shadow-sm mt-3">
              <div className="card-header"><strong>Run History</strong></div>
              <div className="list-group list-group-flush" style={{ maxHeight: 250, overflowY: "auto" }}>
                {runHistory.map((r) => (
                  <button
                    key={r.id}
                    className={`list-group-item list-group-item-action d-flex justify-content-between align-items-center ${activeRun?.id === r.id ? "active" : ""}`}
                    onClick={() => loadRun(r.id)}
                  >
                    <div>
                      <small>{new Date(r.startedAt).toLocaleTimeString()}</small>
                      <br />
                      <small className="text-muted">{r.suiteIds?.length || 0} suites</small>
                    </div>
                    <span
                      className="badge"
                      style={{ backgroundColor: STATUS_BADGE[r.status]?.bg || "#6c757d" }}
                    >
                      {STATUS_BADGE[r.status]?.label || r.status}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* ── CLI reference ───────────────────────────────────────────── */}
          <div className="card shadow-sm mt-3">
            <div className="card-header"><strong>CLI Commands</strong></div>
            <div className="card-body" style={{ fontSize: 12 }}>
              <div className="mb-1"><code>npm test</code> <span className="text-muted">— all suites</span></div>
              <div className="mb-1"><code>npm run test:events</code> <span className="text-muted">— single suite</span></div>
              <div className="mb-1"><code>npm run test:debug</code> <span className="text-muted">— interactive</span></div>
              <div><code>npm run test:report</code> <span className="text-muted">— open HTML report</span></div>
            </div>
          </div>
        </div>

        {/* ── Right: Results ──────────────────────────────────────────────── */}
        <div className="col-lg-8">

          {/* ── Diagnosed error banner ────────────────────────────────────── */}
          {runError && (
            <div className="card shadow-sm mb-3 border-danger">
              <div className="card-body">
                <div className="d-flex align-items-start">
                  <i className="bi bi-exclamation-octagon-fill text-danger me-3" style={{ fontSize: 24, flexShrink: 0 }}></i>
                  <div style={{ minWidth: 0 }}>
                    <h6 className="mb-1 text-danger">{runError.title}</h6>
                    <p className="mb-2" style={{ fontSize: 13 }}>{runError.message}</p>
                    <div
                      style={{
                        backgroundColor: "#f8f9fa",
                        border: "1px solid #dee2e6",
                        borderRadius: 6,
                        padding: "8px 12px",
                        fontSize: 12,
                        fontFamily: "monospace",
                        whiteSpace: "pre-wrap",
                      }}
                    >
                      <strong style={{ color: "#198754" }}>How to fix:</strong>{"\n"}{runError.fix}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {!activeRun && !runError ? (
            <div className="card shadow-sm">
              <div className="card-body text-center py-5 text-muted">
                <i className="bi bi-play-circle" style={{ fontSize: 48 }}></i>
                <p className="mt-3 mb-0">Select test suites and click <strong>Run</strong> to start.</p>
                <p className="mt-1">
                  <small>Tests can also be run via CLI: <code>npm test</code> or <code>npm run test:events</code></small>
                </p>
              </div>
            </div>
          ) : activeRun && (
            <>
              {/* Summary card */}
              <div className="card shadow-sm mb-3">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="mb-0">
                      <span
                        className="badge me-2"
                        style={{ backgroundColor: STATUS_BADGE[activeRun.status]?.bg || "#6c757d" }}
                      >
                        {STATUS_BADGE[activeRun.status]?.label || activeRun.status}
                      </span>
                      Run Results
                    </h5>
                    <div className="text-end">
                      {activeRun.finishedAt && (
                        <small className="text-muted d-block">
                          {Math.round((new Date(activeRun.finishedAt) - new Date(activeRun.startedAt)) / 1000)}s total
                        </small>
                      )}
                      {activeRun.exitCode !== null && activeRun.exitCode !== 0 && (
                        <small className="text-danger">Exit code: {activeRun.exitCode}</small>
                      )}
                    </div>
                  </div>

                  {isRunning && (
                    <div className="progress mb-3" style={{ height: 4 }}>
                      <div
                        className="progress-bar progress-bar-striped progress-bar-animated bg-primary"
                        style={{ width: "100%" }}
                      />
                    </div>
                  )}

                  {activeRun.summary && (
                    <div className="row text-center g-2">
                      {renderSummaryCard("Total", activeRun.summary.total, null)}
                      {renderSummaryCard("Passed", activeRun.summary.passed, "#198754")}
                      {renderSummaryCard("Failed", activeRun.summary.failed, "#dc3545")}
                      {renderSummaryCard("Skipped", activeRun.summary.skipped, "#6c757d")}
                      {renderSummaryCard(
                        "Duration",
                        activeRun.summary.duration ? `${(activeRun.summary.duration / 1000).toFixed(1)}s` : "-",
                        null
                      )}
                    </div>
                  )}

                  {/* No results and no summary — likely a setup/infra error */}
                  {!isRunning && !activeRun.summary && !activeRun.tests?.length && (
                    <div className="alert alert-danger mb-0 mt-2">
                      <i className="bi bi-info-circle me-2"></i>
                      <strong>No test results received.</strong>{" "}
                      {activeRun.rawStderr
                        ? "The test process exited before producing results. Check the raw output below for details."
                        : "The test process may have crashed or Playwright may not be installed correctly."}
                    </div>
                  )}
                </div>
              </div>

              {/* Test results list */}
              {activeRun.tests && activeRun.tests.length > 0 && (
                <div className="card shadow-sm">
                  <div className="card-header d-flex justify-content-between align-items-center">
                    <strong>Test Details</strong>
                    <small className="text-muted">{activeRun.tests.length} test(s)</small>
                  </div>
                  <div className="card-body p-0">
                    {renderTestsBySuite(activeRun.tests)}
                  </div>
                </div>
              )}

              {/* stderr / raw errors */}
              {activeRun.rawStderr && activeRun.status !== "running" && (
                <div className="card shadow-sm mt-3">
                  <div className="card-header d-flex justify-content-between align-items-center">
                    <strong>Raw Output</strong>
                    <small className="text-muted">stderr + process output</small>
                  </div>
                  <div className="card-body p-0">
                    <pre
                      style={{
                        maxHeight: 400,
                        overflow: "auto",
                        fontSize: 12,
                        backgroundColor: "#1e1e1e",
                        color: "#d4d4d4",
                        padding: 16,
                        margin: 0,
                        borderRadius: "0 0 6px 6px",
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                      }}
                    >
                      {activeRun.rawStderr}
                    </pre>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );

  // ── Render helpers ────────────────────────────────────────────────────────

  function renderSummaryCard(label, value, color) {
    return (
      <div className="col">
        <div className="border rounded p-2">
          <div style={{ fontSize: 24, fontWeight: 700, color: color || "inherit" }}>{value}</div>
          <small className="text-muted">{label}</small>
        </div>
      </div>
    );
  }

  function renderTestsBySuite(tests) {
    const bySuite = {};
    for (const t of tests) {
      const key = t.suite || "Unknown";
      if (!bySuite[key]) bySuite[key] = [];
      bySuite[key].push(t);
    }

    return Object.entries(bySuite).map(([suiteName, suiteTests]) => {
      const passed = suiteTests.filter((t) => t.status === "passed").length;
      const failed = suiteTests.filter((t) => t.status === "failed" || t.status === "timedOut").length;
      const skipped = suiteTests.filter((t) => t.status === "skipped").length;
      const isExpanded = expandedSuites.has(suiteName);
      const hasFailures = failed > 0;

      return (
        <div key={suiteName}>
          {/* Suite header */}
          <div
            className="d-flex align-items-center px-3 py-2"
            style={{
              backgroundColor: hasFailures ? "#fff5f5" : "#f8fff8",
              borderBottom: "1px solid #dee2e6",
              cursor: "pointer",
            }}
            onClick={() => toggleSuiteExpand(suiteName)}
          >
            <i className={`bi ${isExpanded ? "bi-chevron-down" : "bi-chevron-right"} me-2`}></i>
            <strong className="me-2" style={{ fontSize: 13 }}>{suiteName}</strong>
            <span className="badge bg-success me-1" title="Passed">{passed}</span>
            {failed > 0 && <span className="badge bg-danger me-1" title="Failed">{failed}</span>}
            {skipped > 0 && <span className="badge bg-secondary me-1" title="Skipped">{skipped}</span>}
            <small className="text-muted ms-auto">
              {suiteTests.length} test{suiteTests.length !== 1 ? "s" : ""}
            </small>
          </div>

          {/* Test rows */}
          {(isExpanded || hasFailures) &&
            suiteTests.map((t, idx) => {
              const globalIdx = tests.indexOf(t);
              const isFail = t.status === "failed" || t.status === "timedOut";
              const isErrorExpanded = expandedErrors.has(globalIdx);

              return (
                <div key={idx}>
                  <div
                    className="d-flex align-items-center px-3 py-2"
                    style={{
                      paddingLeft: 36,
                      borderBottom: "1px solid #f0f0f0",
                      backgroundColor: isFail ? "#fff0f0" : "transparent",
                      cursor: isFail ? "pointer" : "default",
                    }}
                    onClick={() => isFail && toggleError(globalIdx)}
                  >
                    <i
                      className={`bi me-2 ${
                        t.status === "passed"
                          ? "bi-check-circle-fill text-success"
                          : t.status === "skipped"
                          ? "bi-dash-circle text-secondary"
                          : "bi-x-circle-fill text-danger"
                      }`}
                    ></i>
                    <span style={{ fontSize: 13 }}>{t.title}</span>
                    <small className="text-muted ms-auto">
                      {t.duration ? `${(t.duration / 1000).toFixed(2)}s` : ""}
                    </small>
                    {isFail && (
                      <i className={`bi ${isErrorExpanded ? "bi-chevron-up" : "bi-chevron-down"} ms-2 text-danger`}></i>
                    )}
                  </div>

                  {/* Error details */}
                  {isFail && isErrorExpanded && (
                    <div
                      style={{
                        backgroundColor: "#1e1e1e",
                        padding: "12px 16px 12px 48px",
                        fontSize: 12,
                        fontFamily: "monospace",
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                        borderBottom: "1px solid #dee2e6",
                      }}
                    >
                      {t.error ? (
                        <>
                          <div style={{ color: "#f48771", fontWeight: 600, marginBottom: 4 }}>
                            {t.error}
                          </div>
                          {(() => {
                            const diag = diagnoseError("", t.error);
                            return diag ? (
                              <div style={{ color: "#4ec9b0", marginBottom: 8, padding: "6px 8px", backgroundColor: "#1a2a1a", borderRadius: 4 }}>
                                <strong>Likely cause:</strong> {diag.title}{"\n"}
                                <strong>Fix:</strong> {diag.fix}
                              </div>
                            ) : null;
                          })()}
                          {t.errorStack && (
                            <div style={{ color: "#808080", marginTop: 8 }}>
                              {t.errorStack}
                            </div>
                          )}
                        </>
                      ) : (
                        <div style={{ color: "#f48771" }}>
                          No error details captured. Check the Raw Output section below.
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
        </div>
      );
    });
  }
}
